<template>
  <div class="page-content calendar clearfixing add-event">
    <div class="page-title">
      <router-link :to="{ name: 'Calendar' }">
        <span class="link uppercase-text">{{ $lang.messages.calendar }}</span>
      </router-link>
      <span> / </span>
      <span class="text">{{ $lang.messages.addEvent }}</span>
    </div>

    <div class="page-wrapper">
      <form
          class="add-event-form"
          name="addEventForm"
          novalidate
          @submit.prevent="onAddEventFormSubmitted"
          autocomplete="off"
      >
        <input autocomplete="off" name="hidden" type="text" hidden />

        <div class="section-col-wrapper">
          <div class="section-col">
            <!-- CHILDREN -->
            <div class="part">
              <p class="input-filter-label">{{ $lang.messages.child }}</p>
              <div class="wrapper">
                <cases-checklist
                  :items="cases"
                  :placeholder="$lang.messages.selectChildren"
                  :name="'children'"
                  :bluredProp="bluredProp"
                  :option="event.preselectedChildren.length > 0"
                  :disableNeededProp="false"
                  :key="rerender"
                  @optionChanged="onSelectedChildren"
                  @clearInputErrors="clearInputErrors"
                  @clearOthers="clearOthers"
                  ref="childrenChecklist" />
              </div>
            </div>

            <!-- EVENT TITLE -->
            <f-p-input-text
                :inputTextTitle="$lang.messages.eventTitle"
                :inputErrorsName="'eventTitle'"
                :inputName="'eventTitle'"
                :inputPlaceholder="$lang.messages.eventTitlePlaceholder"
                :inputValue="event.eventTitle"
                :inputTextScopeValidation="''"
                :maxLength="255"
                :bluredProp="bluredProp"
                @inputTextValue="inputTextValue"
                @clearInputErrorsText="clearInputErrors"
                @clearOthersText="clearOthers">
            </f-p-input-text>

            <!-- NOTE -->
            <f-p-textarea-note
              :noteHeadline="$lang.messages.additionalDescription"
              :textareaValue="event.additionalDescription"
              :noteType="$lang.messages.additionalDescriptionPlaceholder"
              @textareaInput="textareaInput">
            </f-p-textarea-note>

            <!-- ATTACHMENT -->
            <f-p-input-upload
                :attachments="event.attachments"
                :uploadedAttachments="event.eventAttachments"
                :actionType="actionType"
                @deleteAttachment="deleteAttachment"
                @deleteUploadAttachment="deleteUploadAttachment"
                @onChangeFile="onChangeFile">
            </f-p-input-upload>

          </div>

          <div class="section-col">

            <!-- DATEPICKER -->
            <div class="part datepicker-part">
              <p class="input-filter-label">{{ $lang.messages.date }}</p>
              <f-p-datepicker
                  :option="event.date.toString()"
                  @optionChanged="onSelectedEventDate" >
              </f-p-datepicker>
            </div>

            <!-- TIMEPICKER -->
            <div class="part timepicker-part">
              <p class="input-filter-label">{{ $lang.messages.time }}</p>
              <div class="wrapper">
                <timepicker
                    :items="eventTimeList"
                    :option="event.eventTime"
                    :key="rerender"
                    ref="eventTime"
                    @optionChanged="onSelectedEventTime"
                    @click="onClickEventTime">
                </timepicker>
                <img
                    v-show="(errors.has('eventTime') && !blurredTimepickerProp)"
                    src="@/assets/images/validation_ico.png"
                    alt="Red exclamation mark"
                    class="error-icon" />
                <div class="error-tooltip" v-show="errors.has('eventTime')">
                  <div class="text">
                    {{ errors.first('eventTime') }}
                  </div>
                </div>
              </div>
            </div>

            <!-- LOCATION -->
            <div class="part">
              <p class="input-filter-label">{{ $lang.messages.location }}</p>
              <div
                class="input-wrapper input-field"
                :class="{'error-input' : errors.has('location')}">
                <input
                  type="text"
                  name="location"
                  class="input"
                  :placeholder="$lang.messages.locationPlaceholder"
                  v-model="event.location"
                  v-validate="'max:32'"
                  @focus.self="clearInputErrors"
                  @blur="clearOthers"
                />
                <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon">

                <div
                    v-show="errors.has('location')"
                    class="error-tooltip"
                    :class="{'hide-tooltip' : bluredProp !== 'location'}"
                >
                  <div class="text">
                    {{ errors.first('location') }}
                  </div>
                </div>
              </div>
            </div>

            <!-- RECURRING EVENT -->
            <div class="part recurring-label-part">
              <label class="checkbox-wrapper calendar">
                {{ $lang.messages.recurringEvent }}
                <input type="checkbox" v-model="event.recurringEvent" @click="setRecurringEvent">
                <span class="checkmark"></span>
              </label>
              <hr>
            </div>

            <div :class="{'disabled' : !event.recurringEvent}">
              <!-- FREQUENCY -->
              <div class="part middle-part">
                <p class="input-filter-label">{{ $lang.messages.repeatEvery }}</p>
                <div class="input-wrapper extra-short-input-wrapper" :class="{'error-input' : errors.has('repeatDuration')}">
                  <input
                    type="text"
                    class="input"
                    name="repeatDuration"
                    placeholder="1"
                    v-model="event.repeatDuration"
                    @focus="clearInputErrors"
                    @blur="clearOthers"
                    v-validate="'min_value:1|numeric|max_value:100'"
                    :min="1"
                    :max="100"
                    :disabled="!event.recurringEvent"
                  />
                  <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon error-icon">
                  <div class="error-tooltip" v-show="errors.has('repeatDuration')" :class="{'hide-tooltip' : bluredProp !== 'repeatDuration'}">
                    <div class="text">
                      {{ errors.first('repeatDuration') }}
                    </div>
                  </div>
                </div>
                <div class="wrapper short-input-wrapper">
                  <autocomplete
                      v-if="event.repeatUnit !== ''"
                      :items="repeatUnits"
                      :placeholder="$lang.messages.week"
                      :option="event.repeatUnit"
                      :name="'repeatUnit'"
                      :bluredProp="bluredProp"
                      :middleSize="true"
                      :disabledProp="event.recurringEvent"
                      :key="rerender"
                      @optionChanged="onSelectedRepeatUnit"
                      @clearInputErrors="clearInputErrors"
                      @clearOthers="clearOthers"
                      ref="autocompleteRepeatUnit"/>
                </div>
              </div>

              <div class="part">
                <div v-if="event.repeatUnit.value === 'WEEK'">
                  <p class="input-filter-label">{{ $lang.messages.repeatOn }}</p>
                  <div class="circle-row" :class="{'circle-row-disabled' : !event.recurringEvent}">
                    <div class="circle" :class="{'active' : event.monday, 'disabled-click' : !event.recurringEvent}" @click="event.monday = !event.monday">
                      {{ $lang.messages.monAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.tuesday, 'disabled-click' : !event.recurringEvent}" @click="event.tuesday = !event.tuesday">
                      {{ $lang.messages.tueAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.wednesday, 'disabled-click' : !event.recurringEvent}" @click="event.wednesday = !event.wednesday">
                      {{ $lang.messages.wedAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.thursday, 'disabled-click' : !event.recurringEvent}" @click="event.thursday = !event.thursday">
                      {{ $lang.messages.thuAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.friday, 'disabled-click' : !event.recurringEvent}" @click="event.friday = !event.friday">
                      {{ $lang.messages.friAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.saturday, 'disabled-click' : !event.recurringEvent}" @click="event.saturday = !event.saturday">
                      {{ $lang.messages.satAbbr }}
                    </div>
                    <div class="circle" :class="{'active' : event.sunday, 'disabled-click' : !event.recurringEvent}" @click="event.sunday = !event.sunday">
                      {{ $lang.messages.sunAbbr }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- END PERIOD-->
              <div class="part recurring-end-period">
                <p class="input-filter-label">{{ $lang.messages.end }}</p>
                <div class="sr-wrapper extra-short-input-wrapper">
                  <autocomplete
                      v-if="event.endUnit !== ''"
                      :items="endUnits"
                      :placeholder="$lang.messages.on"
                      :name="'endUnit'"
                      :option="event.endUnit"
                      :bluredProp="bluredProp"
                      :smallSize="true"
                      :key="rerender"
                      @optionChanged="onSelectedEndUnit"
                      @clearInputErrors="clearInputErrors"
                      @clearOthers="clearOthers"
                      ref="autocompleteEventEndUnit"/>
                </div>

                <div class="part datepicker-part">
                  <f-p-datepicker
                      v-if="event.endUnit.value === 'on'"
                      :option="event.endDate.toString()"
                      :forbidFromComparedDate="true"
                      :comparedDate="new Date(event.date)"
                      @optionChanged="onSelectedEventEndDate" >
                  </f-p-datepicker>
                </div>

                <div class="input-wrapper special-short-input-wrapper" v-if="event.endUnit.value === 'after'" :class="{'error-input' : errors.has('endDuration')}">
                  <input
                    type="text"
                    class="input"
                    name="endDuration"
                    v-model="event.endDuration"
                    @focus="clearInputErrors"
                    @blur="clearOthers"
                    v-validate="'min_value:1|numeric|max_value:100'"
                    :min="1"
                    :max="100"
                  />
                  <img src="@/assets/images/validation_ico.png" alt="" class="icon error-icon error-icon">
                  <div class="error-tooltip" v-show="errors.has('endDuration')" :class="{'hide-tooltip' : bluredProp !== 'endDuration'}">
                    <div class="text">
                      {{ errors.first('endDuration') }}
                    </div>
                  </div>
                </div>
                <div class="text-wrapper" v-if="event.endUnit.value === 'after'">{{ $lang.messages.occurrences }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="section-bottom">
          <hr class="horizontal-line">
          <f-p-cancel-button :breadcrumbTo="'Calendar'" :cancelTitle="$lang.messages.cancel"></f-p-cancel-button>
          <button type="submit" class="action-btn">
            <span v-show="!loading">{{ $lang.messages.schedule }}</span>
            <ClipLoader :loading="loading" :color="'#fff'" :size="'20px'"></ClipLoader>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import { ADD_EVENT, GET_SINGLE_EVENT, EDIT_SINGLE_EVENT } from "@/store/action.types";
  import { dateFormat, requestDateFormatLocalDate, requestDateFormatUTCTime, requestDateFormatEndOfDay } from '@/mixins/dateFormat';
  import { addMonths } from '@/mixins/addMonths';
  import { addYears } from '@/mixins/addYears';
  import Autocomplete from "@/components/common/Autocomplete";
  import CasesChecklist from "@/components/common/CasesChecklist";
  import Timepicker from "@/components/common/Timepicker";
  import FPDatepicker from '@/components/common/FPDatepicker';
  import FPInputEditUpload from "@/components/common/inputs/FPInputEditUpload";
  import FPInputUpload from "@/components/common/inputs/FPInputUpload";
  import FPTextareaNote from "@/components/common/inputs/FPTextareaNote";
  import FPInputTextValidate from "@/components/common/inputs/FPInputTextValidate";
  import ClipLoader from 'vue-spinner/src/ClipLoader';
  import FPCancelButton from "@/components/common/FPCancelButton";
  import STATUS_CODES from "@/common/statusCodes";

  export default {
    name: "addEvent",
    components: {
      Autocomplete,
      CasesChecklist,
      FPDatepicker,
      ClipLoader,
      Timepicker,
      'f-p-input-edit-upload': FPInputEditUpload,
      'f-p-input-upload': FPInputUpload,
      'f-p-textarea-note': FPTextareaNote,
      'f-p-input-text': FPInputTextValidate,
      'f-p-cancel-button': FPCancelButton
    },
    mixins: [
      dateFormat,
      requestDateFormatUTCTime,
      requestDateFormatEndOfDay,
      requestDateFormatLocalDate,
      addMonths,
      addYears
    ],
    data() {
      return {
        eventGuid: this.$route.params.id,
        event: {
          preselectedChildren: [],
          children: [],
          coparents: [],
          eventTitle: '',
          additionalDescription: '',
          attachments: [],
          eventAttachments: [],
          deletedAttachments: [],
          date: new Date(),
          eventTime: '',
          location: '',
          recurringEvent: false,
          repeatDuration: 1,
          repeatUnit: '',
          endDuration: 1,
          endUnit: '',
          endDate: new Date(),
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        },
        bluredProp: '',
        eventTimeList: [
          { name: '12:00 AM', value: '00:00'}, { name: '01:00 AM', value: '01:00'}, { name: '02:00 AM', value: '02:00'},
          { name: '03:00 AM', value: '03:00'}, { name: '04:00 AM', value: '04:00'}, { name: '05:00 AM', value: '05:00'},
          { name: '06:00 AM', value: '06:00'}, { name: '07:00 AM', value: '07:00'}, { name: '08:00 AM', value: '08:00'},
          { name: '09:00 AM', value: '09:00'}, { name: '10:00 AM', value: '10:00'}, { name: '11:00 AM', value: '11:00'},
          { name: '12:00 PM', value: '12:00'}, { name: '01:00 PM', value: '13:00'}, { name: '02:00 PM', value: '14:00'},
          { name: '03:00 PM', value: '15:00'}, { name: '04:00 PM', value: '16:00'}, { name: '05:00 PM', value: '17:00'},
          { name: '06:00 PM', value: '18:00'}, { name: '07:00 PM', value: '19:00'}, { name: '08:00 PM', value: '20:00'},
          { name: '09:00 PM', value: '21:00'}, { name: '10:00 PM', value: '22:00'}, { name: '11:00 PM', value: '23:00'}
        ],
        repeatUnits: [
          {
            value: 'DAY',
            name: 'day'
          },
          {
            value: 'WEEK',
            name: 'week'
          },
          {
            value: 'MONTH',
            name: 'month'
          },
          {
            value: 'YEAR',
            name: 'year'
          }
        ],
        endUnits: [
          {
            value: 'on',
            name: 'On'
          },
          {
            value: 'after',
            name: 'After'
          }
        ],
        rerender: 0,
        actionType: 'add',
        blurredTimepickerProp: false,
        loading: false,
      };
    },
    computed: {
      children() {
        let children = this.$store.getters.cases.reduce((children, caseIt) => children.concat(caseIt.children.map(child => child)), []);
        children.forEach(child => child.clicked = false);
        return children
      },
      cases() {
        return this.$store.getters.cases.filter(item => {
          return item.children.length > 0
        })
      },
    },
    watch: {
      'children': function () {
        this.autofillChildren()
      }
    },
    mounted() {
      if (this.eventGuid !== undefined) {
        this.loadEvent()
        this.actionType = 'edit'
      }

      this.event.children = []
      this.event.preselectedChildren = []
      this.autofillChildren()

      this.event.repeatUnit = this.repeatUnits[0]
      this.event.endUnit = this.endUnits[0];
      this.$root.$on('add-event', this.onAddEventFormSubmitted);
    },
    destroyed() {
      this.$root.$off('add-event', this.onAddEventFormSubmitted);
    },
    methods: {
      ...mapActions({
        addEventAction: ADD_EVENT,
        getSingleEvent: GET_SINGLE_EVENT,
        editEventAction: EDIT_SINGLE_EVENT
      }),
      /**
       * @description Methods send request to BE with event guid, then parse response to event object and rerender page with filled form.
       **/
      loadEvent() {
        this.getSingleEvent({
          guid: this.eventGuid
        })
        .then(response => {
          let data = response.data.data;
          let zoneId = data.zoneId === 'UTC' ? 'Z' : data.zoneId;
          this.event.preselectedChildren = this.matchChildren(data.childrenGuid);
          this.event.eventTitle = data.eventTitle;
          this.event.additionalDescription = data.description;
          this.event.date = new Date(data.startDate + zoneId);
          this.event.location = data.location;
          this.event.recurringEvent = data.endDate !== null;
          this.event.repeatDuration = data.intervalDuration === null ? 1 : data.intervalDuration;
          this.event.repeatUnit =  data.intervalUnit != null ? this.repeatUnits.find(unit => unit.value === data.intervalUnit) : this.repeatUnits[0];
          this.event.monday = data.monday;
          this.event.tuesday = data.tuesday;
          this.event.wednesday = data.wednesday;
          this.event.thursday = data.thursday;
          this.event.friday = data.friday;
          this.event.saturday = data.saturday;
          this.event.sunday = data.sunday;

          if (data.numberOfOccurrences !== null) {
            this.event.endDuration = data.numberOfOccurrences;
            this.event.endUnit = this.endUnits[1]
          } else {
            this.event.endDate = new Date(data.endDate);
            this.event.endUnit = this.endUnits[0]
          }

          this.onSelectedChildren(this.event.preselectedChildren);
          let timeString = this.event.date.getHours() <= 9 ? '0' + this.event.date.getHours() : this.event.date.getHours();
          timeString+=':';
          timeString+= this.event.date.getMinutes() <= 9 ? '0' + this.event.date.getMinutes() : this.event.date.getMinutes();
          this.onSelectedEventTime(timeString);

          this.event.eventAttachments = data.attachmentResponseDtos;

          this.rerender+=1
        })
        .catch(error => {
          if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
            this.$snotify.error(this.$lang.messages.sn_genericError, 'ERROR');
          }
        });
      },
      matchChildren(childrenGuid) {
        let childrenList = []
        childrenGuid.forEach(child => {
          let found = this.findChildName(child)
          found.clicked = true
          found.coparentGuid = this.findCoparent(child)
          childrenList.push(found)
        })

        return childrenList
      },
      findChildName(guid) {
        return this.children.find(child => child.childGuid === guid)
      },
      /**
       * @description Find coparent guid by children guid
       * @param {String} childGuid
       * @return {String} - Coparent guid
       */
      findCoparent(childGuid) {
        let coparent = '';

        this.cases.forEach(singleCase => {
          singleCase.children.forEach(child => {
            if (child.childGuid === childGuid) {
              coparent = singleCase.coparentGuid
              return
            }
          })
        })

        return coparent
      },
      clearInputErrors(e) {
        this.$validator.errors.remove(e.target.name)
      },
      checkAnyInput() {
        this.bluredProp = ''
      },
      clearOthers(e) {
        this.bluredProp = e.target.name
      },
      onSelectedChildren(results) {
        this.event.children = []
        this.event.coparents = []

        results.forEach(element => {
          if (element.children) {
            element.children.forEach(child => {
              if (child.clicked === true) {
                this.event.children.push(child.childGuid)

                if (!this.event.coparents.includes(element.coparentGuid)) {
                  this.event.coparents.push(element.coparentGuid)
                }
              }
            })
          } else {
            if (element.clicked === true) {
              this.event.children.push(element.childGuid)

              if (!this.event.coparents.includes(element.coparentGuid)) {
                this.event.coparents.push(element.coparentGuid)
              }
            }
          }
        })
      },
      onSelectedRepeatUnit(value) {
        if (value.name === 'day' || value.name === 'week' || value.name === 'month' || value.name === 'year') {
          this.event.repeatUnit = value
        }
      },
      onSelectedEndUnit(value) {
        if (value.value === 'on' || value.value === 'after') {
          this.event.endUnit = value
        }
      },
      onSelectedEventTime(value) {
        if(value !== '') {
          this.event.eventTime = value;
          this.event.date.setHours(this.event.eventTime.split(':')[0]);
          this.event.date.setMinutes(this.event.eventTime.split(':')[1]);
        } else {
          this.event.eventTime = '';
        }
        this.validateTime();
      },
      validateTime() {
        if (this.event.eventTime === '') {
          this.$validator.errors.add({
            field: 'eventTime',
            msg: `${this.$lang.messages.vee_the} ${this.$lang.messages.vee_eventTime} ${this.$lang.messages.vee_requiredField}.`
          });
          this.$refs.eventTime.setError(true);
          return false;
        } else {
          this.$validator.errors.remove('eventTime');
          this.$refs.eventTime.setError(false);
          return true;
        }
      },
      onClickEventTime(value) {
        this.blurredTimepickerProp = value;
      },
      /**
       * @description Method sets event start date in event object when user choose it in datepicker. Also it sets event time to loaded time, if action is edit.
       * @param {Date} value -start date from datepicker
       **/
      onSelectedEventDate(value) {
        this.event.date = new Date(value)
        if (new Date(this.event.date) > new Date(this.event.endDate)) {
          this.onSelectedEventEndDate(this.event.date);
        }
        if (this.eventGuid !== undefined) {
          const timeString = this.event.eventTime.split(":");
          this.event.date.setHours(timeString[0]);
          this.event.date.setMinutes(timeString[1])
          this.event.date.setSeconds(0, 0);
        }
      },
      /**
       * @description Method sets event end date in event object when user choose it in datepicker.
       * @param {Date} value - end date from datepicker
       **/
      onSelectedEventEndDate(value) {
        this.event.endDate = new Date(value)
      },
      /**
       * @description Method validates form and sets error if some field is not correct.
       * If validation result is true, it calls methods for adding and editing event depending on existence of guid.
       **/
      onAddEventFormSubmitted() {
        this.bluredProp = '';
        this.$validator.validateAll().then(result => {
          this.event.zonedStartTimeDate = this.requestDateFormatUTCTime(this.event.date.setSeconds(0, 0));
          let eventTimeValid = this.validateTime();

          let childrenValid = this.checkChildrenValidity(this.event.children);
          if (!childrenValid) {
            this.$validator.errors.add({
              field: "children",
              msg: `${this.$lang.vee_selectValidField} ${this.$lang.messages.children.toLowerCase()}.`
            });
            this.$refs.childrenChecklist.setValidateField()
          }

          if (result && childrenValid && eventTimeValid) {
            if (this.eventGuid === undefined) {
              this.addSingleEvent()
            } else {
              this.editSingleEvent()
            }
          }
        })
      },
      /**
       * @description Method sends request to BE to add event, with mapped request header with data from event object.
       * If request is successful, it shows success toast and switch user to Calendar page.
       **/
      addSingleEvent() {
        if (!this.loading) {
          this.loading = true;
          this.addEventAction({
            "eventTitle": this.event.eventTitle,
            "description": this.event.additionalDescription,
            "location": this.event.location,
            "startDate": this.event.zonedStartTimeDate,
            "endDate": this.event.recurringEvent && this.event.endUnit.value === 'on' ? this.requestDateFormatLocalDate(this.event.endDate) : null,
            "childrenGuid": this.event.children,
            "coparentGuids": this.event.coparents,
            "intervalDuration": this.event.recurringEvent ? this.event.repeatDuration : null,
            "intervalUnit": this.event.recurringEvent ? this.event.repeatUnit.value : null,
            "monday": this.event.recurringEvent ? this.event.monday : null,
            "tuesday": this.event.recurringEvent ? this.event.tuesday : null,
            "wednesday": this.event.recurringEvent ? this.event.wednesday : null,
            "thursday": this.event.recurringEvent ? this.event.thursday : null,
            "friday": this.event.recurringEvent ? this.event.friday : null,
            "saturday": this.event.recurringEvent ? this.event.saturday : null,
            "sunday": this.event.recurringEvent ? this.event.sunday : null,
            "attachmentRequestDtos": this.event.attachments,
            "numberOfOccurrences": this.event.recurringEvent && this.event.endUnit.value === 'after' ? this.event.endDuration : null,
          })
          .then(() => {
            this.$snotify.success(this.$lang.messages.sn_eventSavedSuccess, 'SUCCESS');
            this.loading = false;
            this.$router.push({name: "Calendar"});
          })
          .catch(error => {
            this.loading = false;
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, 'ERROR');
            }
          });
        }
      },
      /**
       * @description Method sends request to BE to edit event, with mapped request header with data from event object.
       * If request is successful, it shows success toast and switch user to Calendar page.
       **/
      editSingleEvent() {
        if (!this.loading) {
          this.loading = true;
          this.editEventAction({
            "guid": this.eventGuid,
            "eventTitle": this.event.eventTitle,
            "description": this.event.additionalDescription,
            "location": this.event.location,
            "startDate": this.event.zonedStartTimeDate,
            "endDate": this.event.recurringEvent && this.event.endUnit.value === 'on' ? this.requestDateFormatLocalDate(this.event.endDate) : null,
            "childrenGuid": this.event.children,
            "coparentGuids": this.event.coparents,
            "intervalDuration": this.event.recurringEvent ? this.event.repeatDuration : null,
            "intervalUnit": this.event.recurringEvent ? this.event.repeatUnit.value : null,
            "monday": this.event.recurringEvent ? this.event.monday : null,
            "tuesday": this.event.recurringEvent ? this.event.tuesday : null,
            "wednesday": this.event.recurringEvent ? this.event.wednesday : null,
            "thursday": this.event.recurringEvent ? this.event.thursday : null,
            "friday": this.event.recurringEvent ? this.event.friday : null,
            "saturday": this.event.recurringEvent ? this.event.saturday : null,
            "sunday": this.event.recurringEvent ? this.event.sunday : null,
            "attachmentRequestDtos": this.event.attachments,
            "attachmentGuidsForDeletion": this.event.deletedAttachments,
            "numberOfOccurrences": this.event.recurringEvent && this.event.endUnit.value === 'after' ? this.event.endDuration : null,
          })
          .then(() => {
            this.$snotify.success(this.$lang.messages.sn_eventUpdatedSuccess, 'SUCCESS');
            this.loading = false;
            this.$router.push({name: "Calendar"});
          })
          .catch(error => {
            this.loading = false;
            if (error.response.status !== 406 && error.response.data.status.code !== STATUS_CODES.FP_405 && error.response.status !== 401 && error.response.status !== 402) {
              this.$snotify.error(error.response.data.status.desc, 'ERROR');
            }
          });
        }
      },
      setRecurringEvent() {
        this.event.recurringEvent = !this.event.recurringEvent;
        if (this.event.recurringEvent) {
          this.event.endDate = this.event.endDate !== '' ? this.event.endDate : this.event.date;
        }
        else {
          this.event.endDate = this.event.endDate !== '' ? this.event.endDate : '';
        }
      },
      // TODO: Candidate for global (Payments) function or mixin
      deleteAttachment(guid) {
        this.event.deletedAttachments.push(guid);
        this.event.eventAttachments = this.event.eventAttachments.filter(function(value, index, array) {
          return value.attachmentGuid !== guid
        })
      },
      // TODO: Candidate for global (Payments) function or mixin
      deleteUploadAttachment(fileName) {
        this.event.attachments = this.event.attachments.filter(value => {
          return value.fileName !== fileName
        })
      },
      // TODO: Candidate for global (Payments) function or mixin
      onChangeFile(file) {
        if (file !== '') {
          this.event.attachments.push({
            fileName: file.name,
            mimeType: file.type,
            encodedFile: file.base64
          })
        }
      },
      /**
       * @description From child component (FPTextareaNote) listen to the textarea input/value, update the prop
       * and send the value back to the children
       * @param {String} val
       * @return {String}
       */
      textareaInput (val) {
        this.event.additionalDescription = val
      },
      /**
       *
       * @description From child component (FPInputTextValidate) listen to the <input type="text"> value, update
       * the prop and send the value back to the children
       * @param {String} val
       * @returns {String}
       */
      inputTextValue (val) {
        this.event.eventTitle = val;
      },
      checkChildrenValidity(children) {
        let valid = false;
        children.forEach(element => {
          valid = this.checkChildValidity(element)
        });
        return valid
      },
      checkChildValidity(element) {
        let valid = false;
        this.children.forEach(child => {
          if (child.childGuid === element) {
            valid = true
          }
        });
        return valid
      },
      autofillChildren() {
        if(this.eventGuid === undefined && this.children.length === 1) {
          this.children[0].clicked = true
          this.children[0].coparentGuid = this.findCoparent(this.children[0].childGuid)
          this.event.preselectedChildren = this.children
          this.onSelectedChildren(this.event.preselectedChildren)
          this.rerender+=1
        }
      }
    }
  };
</script>
